import React from 'react';
import { MoreHorizontal, Pencil } from 'lucide-react';

import { formatDate, formatMontant } from '../../lib/formats';
import { libelle, texteSur } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { StatutBadge } from '../ui/StatutBadge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { referencePiece } from './origine';

/**
 * Le tableau du compte, en lecture seule: une ligne par mouvement, du plus
 * ancien au plus récent, avec le reste dû après chaque ligne. Une ligne
 * annulée reste visible (barrée) avec la mention de son annulation; la ligne
 * d'annulation est une pastille. Cliquer une ligne ouvre sa pièce d'origine.
 * « N° » est le numéro propre de la ligne (`PKS-AAAA-NNNNN`, `number`),
 * celui du relevé signé; « Référence pièce » reste celle de la pièce.
 */

const estAnnulation = (ligne) => Boolean(ligne.is_reversal) || ligne.type === 'ANNULATION';

/**
 * Observations d'une ligne: une annulation commence par « Annule » et le
 * numéro propre de la ligne annulée (donné par le serveur, même si cette
 * ligne est sur une autre page), suivi du motif.
 */
const observationsLigne = (ligne) => {
  const texte = texteSur(ligne.observations, '');
  if (!estAnnulation(ligne) || !ligne.reversal_of_number) return texte;
  return [`Annule ${ligne.reversal_of_number}`, texte].filter(Boolean).join(' · ');
};

/** « Lot » ou « Location » devant la référence; rien pour une saisie directe. */
const FamilleOrigine = ({ ligne }) => {
  const kind = ligne.origin?.kind;
  if (!kind || kind === 'MANUAL') return null;
  return (
    <span className="mr-1.5 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground" data-testid="famille-origine">
      {libelle('origine_compte', kind)}
    </span>
  );
};

/** Un montant: vide quand il est nul; rouge s'il est négatif. */
const Montant = ({ valeur, devise, barre }) => {
  const nombre = Number(valeur) || 0;
  if (nombre === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn(nombre < 0 && 'text-destructive', barre && 'line-through')}>{formatMontant(nombre, devise)}</span>
  );
};

const TypeOperation = ({ ligne }) =>
  estAnnulation(ligne) ? (
    <StatutBadge domaine="type_operation_compte" code="ANNULATION" ton="destructive" />
  ) : (
    <span className={cn(ligne.reversed && 'line-through')}>{libelle('type_operation_compte', ligne.type)}</span>
  );

const MentionAnnulee = ({ ligne, onVoirAnnulation }) =>
  ligne.reversed ? (
    <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-destructive">
      Annulée
      {ligne.reversed_by_id ? (
        <button
          type="button"
          className="underline underline-offset-2 hover:no-underline"
          onClick={(e) => {
            e.stopPropagation();
            onVoirAnnulation(ligne.reversed_by_id);
          }}
          data-testid="voir-annulation"
        >
          Voir l’annulation
        </button>
      ) : null}
    </span>
  ) : null;

const MenuLigne = ({ ligne, onCorriger }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Actions de la ligne"
        data-testid="menu-ligne"
        onClick={(e) => e.stopPropagation()}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenuItem onSelect={() => onCorriger(ligne)} data-testid="corriger-ligne">
        <Pencil className="h-4 w-4" aria-hidden="true" />
        Corriger
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const peutCorriger = (ligne, estAdmin) => estAdmin && !ligne.reversed && !estAnnulation(ligne);

const classeLigne = (ligne) =>
  cn(
    'cursor-pointer',
    ligne.reversed && 'text-muted-foreground',
    estAnnulation(ligne) && 'bg-destructive/5',
  );

const surClavier = (ouvrir) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    ouvrir();
  }
};

export const CartesCompte = ({ lignes, devise, estAdmin, onOuvrir, onCorriger, onVoirAnnulation }) => (
  <ul className="space-y-2" data-testid="compte-cartes">
    {lignes.map((ligne) => (
      <li key={ligne.id}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onOuvrir(ligne)}
          onKeyDown={surClavier(() => onOuvrir(ligne))}
          className={cn(
            'w-full rounded-xl border border-border/70 bg-card px-3.5 py-3 text-left shadow-sm',
            ligne.reversed && 'text-muted-foreground',
            estAnnulation(ligne) && 'bg-destructive/5',
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">
                <span className={cn('font-mono', ligne.reversed && 'line-through')} data-testid="carte-numero">
                  {ligne.number || '—'}
                </span>
                {' · '}
                {formatDate(ligne.date)}
              </p>
              <div className="text-sm font-semibold">
                <TypeOperation ligne={ligne} />
              </div>
            </div>
            {peutCorriger(ligne, estAdmin) ? <MenuLigne ligne={ligne} onCorriger={onCorriger} /> : null}
          </div>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            <FamilleOrigine ligne={ligne} />
            {referencePiece(ligne) || '—'}
          </p>
          {observationsLigne(ligne) ? <p className="mt-1 text-xs text-muted-foreground">{observationsLigne(ligne)}</p> : null}
          <MentionAnnulee ligne={ligne} onVoirAnnulation={onVoirAnnulation} />
          <dl className="mt-2 grid grid-cols-3 gap-2 text-right text-xs tabular-nums">
            <div>
              <dt className="text-muted-foreground">Avances / Débit</dt>
              <dd><Montant valeur={ligne.debit} devise={devise} barre={ligne.reversed} /></dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Règlements / Crédit</dt>
              <dd><Montant valeur={ligne.credit} devise={devise} barre={ligne.reversed} /></dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Reste dû</dt>
              <dd className={cn('font-semibold', Number(ligne.running_balance) < 0 && 'text-destructive')}>
                {formatMontant(ligne.running_balance, devise)}
              </dd>
            </div>
          </dl>
        </div>
      </li>
    ))}
  </ul>
);

export const TableauCompte = ({ lignes, devise, estAdmin, onOuvrir, onCorriger, onVoirAnnulation }) => (
  <Table compact stickyFirstColumn={false} data-testid="compte-tableau">
    <TableHeader>
      <TableRow>
        <TableHead>N°</TableHead>
        <TableHead>Date</TableHead>
        <TableHead>Type d’opération</TableHead>
        <TableHead>Référence pièce</TableHead>
        <TableHead>Observations</TableHead>
        <TableHead className="text-right">Avances versées / Débit</TableHead>
        <TableHead className="text-right">Règlements / Crédit</TableHead>
        <TableHead className="text-right">Reste dû</TableHead>
        {estAdmin ? (
          <TableHead className="w-10">
            <span className="sr-only">Actions</span>
          </TableHead>
        ) : null}
      </TableRow>
    </TableHeader>
    <TableBody>
      {lignes.map((ligne) => (
        <TableRow
          key={ligne.id}
          className={classeLigne(ligne)}
          tabIndex={0}
          onClick={() => onOuvrir(ligne)}
          onKeyDown={surClavier(() => onOuvrir(ligne))}
          data-testid={`compte-ligne-${ligne.id}`}
          data-annulee={ligne.reversed ? 'oui' : undefined}
        >
          <TableCell className={cn('whitespace-nowrap font-mono text-xs', ligne.reversed && 'line-through')}>
            {ligne.number || '—'}
          </TableCell>
          <TableCell className={cn('whitespace-nowrap', ligne.reversed && 'line-through')}>{formatDate(ligne.date)}</TableCell>
          <TableCell className="whitespace-nowrap">
            <TypeOperation ligne={ligne} />
            <MentionAnnulee ligne={ligne} onVoirAnnulation={onVoirAnnulation} />
          </TableCell>
          <TableCell className="whitespace-nowrap">
            <FamilleOrigine ligne={ligne} />
            <span className={cn(ligne.reversed && 'line-through')}>{referencePiece(ligne) || '—'}</span>
          </TableCell>
          <TableCell
            className={cn('max-w-[18rem] truncate', ligne.reversed && 'line-through')}
            title={observationsLigne(ligne) || undefined}
          >
            {observationsLigne(ligne) || '—'}
          </TableCell>
          <TableCell className="whitespace-nowrap text-right tabular-nums">
            <Montant valeur={ligne.debit} devise={devise} barre={ligne.reversed} />
          </TableCell>
          <TableCell className="whitespace-nowrap text-right tabular-nums">
            <Montant valeur={ligne.credit} devise={devise} barre={ligne.reversed} />
          </TableCell>
          <TableCell
            className={cn(
              'whitespace-nowrap text-right font-semibold tabular-nums',
              Number(ligne.running_balance) < 0 && 'text-destructive',
            )}
          >
            {formatMontant(ligne.running_balance, devise)}
          </TableCell>
          {estAdmin ? (
            <TableCell className="text-right">
              {peutCorriger(ligne, estAdmin) ? <MenuLigne ligne={ligne} onCorriger={onCorriger} /> : null}
            </TableCell>
          ) : null}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default TableauCompte;
